/**
 * Lazy-loads react-gtm-module so it's not in the initial bundle.
 * GTM is only needed after cookie consent or when pushing user data.
 */

import { getUserId } from "./authHeaders";
import { isAnalyticsPathExcluded, isDevEnv } from "./analyticsEnv";
import {
  applyAnalyticsIdentity,
  getAnalyticsUserId,
  getAnalyticsUserStatus,
  inferAnalyticsUserStatusFromCookies,
  setAnalyticsUserStatus,
  type AnalyticsUserStatus,
} from "./analyticsIdentity";

type TagManagerAPI = { initialize: (o: object) => void; dataLayer: (o: object) => void };

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

function isMissingUserId(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}

function getTagManager(mod: { default?: TagManagerAPI } & TagManagerAPI): TagManagerAPI {
  return mod.default ?? mod;
}

let gtmPromise: Promise<{ default?: TagManagerAPI } & TagManagerAPI> | null = null;

function getGtm() {
  if (!gtmPromise) {
    gtmPromise = import("react-gtm-module");
  }
  return gtmPromise;
}

/** Synchronous sticky push so GTM/GA4 can read identity on native events. */
function pushStickyIdentitySync(payload: {
  user_status: AnalyticsUserStatus;
  user_id: string | null;
}) {
  if (typeof window === "undefined" || isDevEnv() || isAnalyticsPathExcluded()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Seed `user_status` (and user_id when known) on the dataLayer before GTM/GA4 starts.
 * Call once when cookie consent is granted, before `initializeGtm` / GTM script inject.
 */
export function seedAnalyticsIdentityBeforeGtm(knownUserId?: string | null) {
  if (isDevEnv() || isAnalyticsPathExcluded()) return;
  if (knownUserId != null && String(knownUserId) !== "") {
    applyAnalyticsIdentity(knownUserId);
  } else if (getAnalyticsUserId()) {
    setAnalyticsUserStatus("logged_in");
  } else {
    const cookieUid = getUserId();
    if (cookieUid) {
      applyAnalyticsIdentity(cookieUid);
    } else {
      // Session/JWT without cr8_userId — status only until UserContext confirms.
      setAnalyticsUserStatus(inferAnalyticsUserStatusFromCookies());
    }
  }
  pushStickyIdentitySync({
    user_status: getAnalyticsUserStatus(),
    user_id: getAnalyticsUserId(),
  });
}

/**
 * Keep sticky dataLayer `user_status` / `user_id` in sync after login or logout.
 * Always pass the current user id (or `null` for guest). Pushes without an `event` key
 * so it does not fire GTM event tags.
 */
export function syncAnalyticsIdentityToDataLayer(userId: string | null) {
  if (isDevEnv() || isAnalyticsPathExcluded()) return;
  applyAnalyticsIdentity(userId);
  pushStickyIdentitySync({
    user_status: getAnalyticsUserStatus(),
    user_id: getAnalyticsUserId(),
  });
}

export function initializeGtm(options: { gtmId: string; events?: Record<string, string> }) {
  if (isAnalyticsPathExcluded()) return Promise.resolve();
  return getGtm().then((mod) => {
    getTagManager(mod).initialize(options);
  });
}

export function pushToDataLayer(data: { dataLayer: Record<string, unknown> }) {
  if (isAnalyticsPathExcluded()) return Promise.resolve();
  const layer = { ...data.dataLayer };
  const globalId = getAnalyticsUserId();
  if (isMissingUserId(layer.user_id) && globalId != null && globalId !== "") {
    layer.user_id = globalId;
  }
  // Sticky identity for native GA4 — fill when caller omitted user_status.
  if (!("user_status" in layer)) {
    layer.user_status = getAnalyticsUserStatus();
  }
  return getGtm().then((mod) => {
    getTagManager(mod).dataLayer({ dataLayer: layer });
  });
}
